import React, { useEffect } from 'react';
import { useCommonAuth } from '../../contexts/CommonAuthContext';
import { config } from '../../config/env';

/**
 * ProductAccessValidator - Validates that the authenticated user has access to a specific product
 *
 * @param {string} requiredProduct - The product key required (e.g., 'athena', 'ares', 'themis')
 * @param {React.ReactNode} children - The component to render if access is granted
 */
const ProductAccessValidator = ({ requiredProduct, children }) => {
  const { user, loading, isAuthenticated } = useCommonAuth();

  useEffect(() => {
    // Wait until authentication check is complete
    if (loading) return;

    // If not authenticated, AuthValidator will handle redirect to login
    if (!isAuthenticated) return;

    // Check if user has access to the required product
    if (user && user.products) {
      const hasAccess = user.products.includes(requiredProduct);
      
      if (!hasAccess) {
        // User is authenticated but doesn't have access to this product
        // Redirect to DASHBOARD (not login) with error message
        const productName = requiredProduct.charAt(0).toUpperCase() + requiredProduct.slice(1);
        const errorMessage = `You do not have access to ${productName}. Please contact your administrator.`;
        console.warn(`❌ Product access denied: User does not have ${requiredProduct} in accessible products:`, user.products);
        
        // Redirect to common auth dashboard with error message
        window.location.href = `${config.COMMON_AUTH_DASHBOARD_URL}?error=${encodeURIComponent(errorMessage)}`;
      } else {
        console.log(`✅ Product access granted: ${requiredProduct}`);
      }
    } else if (user && !user.products) {
      // User authenticated but no products array - deny access
      // Redirect to DASHBOARD (not login) with error message
      const errorMessage = 'You do not have access to any products. Please contact your administrator.';
      console.warn('❌ Product access denied: User has no products array');
      
      // Redirect to common auth dashboard with error message
      window.location.href = `${config.COMMON_AUTH_DASHBOARD_URL}?error=${encodeURIComponent(errorMessage)}`;
    }
  }, [user, loading, isAuthenticated, requiredProduct]);

  // Shell-first: render children while auth validates in the background
  // (the TopProgressBar is the loading signal). The post-load redirect
  // behaviour below is unchanged — a user without access is bounced to
  // the dashboard by the effect above once validation settles.
  if (loading) {
    return children;
  }

  // If not authenticated, let AuthValidator handle redirect
  if (!isAuthenticated) {
    return null;
  }

  // Check access
  if (user && user.products && user.products.includes(requiredProduct)) {
    return children;
  }

  // While redirecting, show nothing
  return null;
};

export default ProductAccessValidator;

